// TODO(ui-foundation): promote this component to ui-foundation and remove from this repo.
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { LoginForm } from "@app/components/organisms/login-form";
import { OtcForm } from "@app/components/organisms/otc-form";
import { VerifyEmailForm } from "@app/components/organisms/verify-email-form";
import { SignupForm } from "@app/components/organisms/signup-form";
import { CloseIcon } from "@app/lib/events-admin-icons";
import { Dialog, Modal, ModalOverlay } from "@app/lib/ui-foundation";
import "./auth-modal.css";

export type AuthModalView = "login" | "signup" | "otc" | "verify";

type VerifyOrigin = "signup" | "login";

type VerificationState = {
  email: string;
  origin: VerifyOrigin;
  statusMessage: string;
};

const EMPTY_VERIFICATION_STATE: VerificationState = {
  email: "",
  origin: "signup",
  statusMessage: "",
};

const AUTH_DIALOG_LABELS: Record<AuthModalView, string> = {
  login: "Login to your account",
  signup: "Create your account",
  otc: "Exchange one-time code",
  verify: "Verify your email",
};

export type AuthModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  initialView?: AuthModalView;
};

export function AuthModal({
  isOpen,
  onOpenChange,
  onSuccess,
  initialView = "login",
}: AuthModalProps) {
  const [view, setView] = useState<AuthModalView>(initialView);
  const [verificationState, setVerificationState] = useState<VerificationState>(
    EMPTY_VERIFICATION_STATE,
  );

  // Reset to the requested initial view only when the modal opens so the user
  // doesn't see the inner subtree flip back to the default during the close
  // animation.
  useEffect(() => {
    if (isOpen) {
      setView(initialView);
      setVerificationState(EMPTY_VERIFICATION_STATE);
    }
  }, [initialView, isOpen]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const switchToSignup = useCallback(() => {
    setVerificationState((current) => ({ ...current, statusMessage: "" }));
    setView("signup");
  }, []);

  const switchToLogin = useCallback(() => {
    setVerificationState((current) => ({ ...current, statusMessage: "" }));
    setView("login");
  }, []);

  // Close the modal as part of any successful auth so callers don't have to
  // remember to invoke onOpenChange(false) alongside their onSuccess handler.
  const handleSuccess = useCallback(() => {
    onOpenChange(false);
    onSuccess();
  }, [onOpenChange, onSuccess]);

  const handleVerificationRequired = useCallback(
    ({ email, statusMessage }: { email: string; statusMessage?: string }) => {
      setVerificationState({
        email,
        origin: "login",
        statusMessage: statusMessage ?? "",
      });
      setView("verify");
    },
    [],
  );

  const handleUnconfirmedSignup = useCallback(
    ({ email }: { email: string }) => {
      setVerificationState({
        email,
        origin: "signup",
        statusMessage: "",
      });
      setView("verify");
    },
    [],
  );

  const handleVerifyStartOver = useCallback(() => {
    if (verificationState.origin === "login") {
      setVerificationState((current) => ({ ...current, statusMessage: "" }));
      setView("login");
      return;
    }

    setVerificationState(EMPTY_VERIFICATION_STATE);
    setView("signup");
  }, [verificationState.origin]);

  const dialogLabel = AUTH_DIALOG_LABELS[view];

  const views: Record<AuthModalView, () => ReactNode> = {
    login: () => (
      <LoginForm
        onSuccess={handleSuccess}
        onSwitchToSignup={switchToSignup}
        onVerificationRequired={handleVerificationRequired}
        initialEmail={verificationState.email}
      />
    ),
    signup: () => (
      <SignupForm
        onSwitchToLogin={switchToLogin}
        onUnconfirmedSignup={handleUnconfirmedSignup}
        onPreConfirmedSignup={switchToLogin}
      />
    ),
    verify: () => (
      <VerifyEmailForm
        email={verificationState.email}
        initialStatusMessage={verificationState.statusMessage}
        onConfirmed={switchToLogin}
        onStartOver={handleVerifyStartOver}
      />
    ),
    otc: () => (
      <OtcForm onSuccess={handleSuccess} onSwitchToLogin={switchToLogin} />
    ),
  };

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} isDismissable>
      <Modal className="events-modal events-modal--auth">
        <Dialog className="events-dialog" aria-label={dialogLabel}>
          <button
            type="button"
            className="events-dialog__close"
            onClick={handleClose}
            aria-label="Close auth modal"
          >
            <CloseIcon className="size-6" />
          </button>

          <div className="events-auth-card">{views[view]()}</div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
